<template>
  <banners
    :sticky-top="true"
    :narrow="true"
    :is-ultra="true"
  >
    <div class="w-full">
      <div
        v-if="loading"
        class="mx-auto flex h-80 max-w-2xl items-center justify-center py-12 sm:overflow-scroll md:overflow-hidden"
      >
        <img
          class="-top-2px loading relative h-12 w-full"
          src="@/assets/loading-v2-yellow.svg"
          alt=""
        />
      </div>
      <div
        v-if="!loading"
        class="relative w-full"
      >
        <div class="container-max mx-auto w-full px-4 py-10 text-left md:px-10">
          <div>
            <div class="flex w-full items-end justify-between pb-8">
              <p class="header-text leading-tight text-dark-blue">My Brokers</p>
            </div>
            <div
              v-if="connections?.length > 0"
              class="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
            >
              <div
                v-for="connection in connections"
                :key="connection?.id"
                class="flex h-full w-full flex-col"
              >
                <conditional-wrapper
                  :condition="linkable_broker && connection.carrier_id"
                  wrapper="router-link"
                  :wrapper-props="{
                    to: {
                      name: 'Carrier Brokers Show Page',
                      params: { id: connection.carrier_id },
                    },
                    target: '_blank',
                  }"
                >
                  <div
                    class="flex h-full w-full flex-col overflow-hidden rounded-lg border border-light-gray shadow-primary hover:shadow-primary-bold"
                  >
                    <div class="flex h-full flex-col">
                      <div
                        class="relative flex h-36 w-full items-center justify-center overflow-hidden border-b border-light-gray bg-white pb-4 pt-8"
                      >
                        <div
                          class="absolute right-1 top-1.5 flex items-center justify-between"
                        >
                          <div
                            class="flex h-10 items-center overflow-hidden rounded-full"
                          >
                            <img
                              class="position relative h-12 w-12"
                              style="top: 0.2rem"
                              src="@/assets/highway-badge-2.svg"
                              alt="highway badge"
                            />
                          </div>
                        </div>
                        <div
                          v-if="connection?.logo?.url"
                          class="relative flex flex-col items-center gap-1"
                        >
                          <img
                            :src="connection?.logo?.url"
                            :alt="connection?.legal_name"
                            class="max-h-24 max-w-70pct"
                            style="margin-bottom: 0px"
                          />
                        </div>
                        <svg
                          v-else
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          class="h-full w-auto text-light-gray"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </div>
                      <div
                        class="flex flex-grow flex-col justify-between gap-3 bg-dark-gray p-4"
                      >
                        <div>
                          <div class="flex items-start justify-between gap-1.5">
                            <p
                              class="text-base font-bold leading-5 text-off-white"
                            >
                              {{ connection?.legal_name }}
                            </p>
                            <div class="inline-block flex-shrink-0">
                              <div>
                                <p
                                  v-if="
                                    connection?.overall_rule_result === 'fail'
                                  "
                                  class="fail-status whitespace-nowrap"
                                >
                                  Access Disabled
                                </p>
                                <p
                                  v-else-if="
                                    connection?.overall_rule_result ===
                                    'incomplete'
                                  "
                                  class="incomplete-status whitespace-nowrap"
                                >
                                  Access Pending
                                </p>
                                <p
                                  v-else
                                  class="pass-status whitespace-nowrap"
                                >
                                  Access Enabled
                                </p>
                              </div>
                            </div>
                          </div>
                          <div class="pb-4">
                            <p class="mb-1 text-xxs text-gray-over-dark">
                              Connected
                              {{
                                formatDate(connection?.connection_created_at)
                              }}
                            </p>
                            <div
                              class="inline-block max-w-full overflow-hidden text-ellipsis"
                            >
                              <a
                                v-if="connection?.support_email"
                                :href="`mailto:${connection?.support_email}`"
                                class="group relative -left-2 flex transform items-center gap-1.5 rounded-full px-2 py-1 text-yellow-500 transition ease-in-out hover:translate-x-2 hover:bg-yellow-500 hover:bg-opacity-30"
                              >
                                <svg
                                  class="h-3 w-3 flex-shrink-0"
                                  viewBox="0 0 16 17"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  data-v-01ef35fe=""
                                >
                                  <path
                                    d="M2.66671 3.05762H13.3334C14.0667 3.05762 14.6667 3.65762 14.6667 4.39095V12.391C14.6667 13.1243 14.0667 13.7243 13.3334 13.7243H2.66671C1.93337 13.7243 1.33337 13.1243 1.33337 12.391V4.39095C1.33337 3.65762 1.93337 3.05762 2.66671 3.05762Z"
                                    stroke="currentColor"
                                    stroke-width="1.33333"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    data-v-01ef35fe=""
                                  />
                                  <path
                                    d="M14.6667 4.39062L8.00004 9.05729L1.33337 4.39062"
                                    stroke="currentColor"
                                    stroke-width="1.33333"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    data-v-01ef35fe=""
                                  />
                                </svg>
                                <span
                                  class="text-ellipsis whitespace-nowrap text-xxs"
                                >
                                  {{ connection?.support_email }}
                                </span>
                              </a>
                            </div>
                          </div>
                        </div>
                        <button-main
                          :variant="
                            isAssessmentPass(connection?.overall_rule_result)
                              ? 'primary'
                              : 'secondary-over-dark'
                          "
                          class="portal-btn group cursor-pointer bg-dark-gray"
                          @click="goToBrokerPortal(connection)"
                        >
                          <span
                            v-if="
                              isAssessmentPass(connection?.overall_rule_result)
                            "
                          >
                            Enter
                          </span>
                          <span v-else>View Status</span>
                          <svg
                            class="tester link-icon inline"
                            :class="
                              isAssessmentPass(connection?.overall_rule_result)
                                ? 'text-white'
                                : ''
                            "
                            width="25"
                            height="24"
                            viewBox="0 0 25 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12.5 6H7.5C6.94772 6 6.5 6.44772 6.5 7V17C6.5 17.5523 6.94772 18 7.5 18H17.5C18.0523 18 18.5 17.5523 18.5 17V12"
                              stroke="currentColor"
                              stroke-width="2"
                            />
                            <path
                              d="M18.6748 5.78174L14.0262 10.4304M18.6748 5.78174L18.6748 9.43214M18.6748 5.78174L15.0244 5.78174"
                              stroke="currentColor"
                              stroke-width="2"
                            />
                          </svg>
                        </button-main>
                      </div>
                    </div>
                  </div>
                </conditional-wrapper>
              </div>
            </div>
            <div
              v-else
              class="mt-6 rounded-lg bg-off-white px-6 pb-8 pt-8"
            >
              <p class="notice-text">
                You currently do not have any connections with Brokers.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="!loading"
        class="relative w-full"
      >
        <div class="absolute left-0 right-0 top-0 h-72 w-full bg-off-white" />
        <div
          class="container-max relative mx-auto w-full px-4 py-10 text-left md:px-10"
        >
          <div class="container-max">
            <p class="header-text mb-8 text-dark-blue">Available Connections</p>
            <section
              v-if="availableConnections?.length > 0"
              class="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
            >
              <div
                v-for="(connection, idx) in availableConnections"
                :key="idx"
                class="mb-6 flex flex-col overflow-hidden rounded-lg border border-light-gray shadow-primary hover:shadow-primary-bold"
              >
                <div
                  class="relative flex h-36 w-full items-center justify-center border-b border-light-gray bg-white py-4"
                >
                  <img
                    v-if="connection?.logo?.url"
                    :src="connection?.logo?.url"
                    :alt="connection?.legal_name"
                    class="max-h-24 max-w-70pct"
                    style="margin-bottom: 0px"
                  />
                  <!-- <img
                      v-else
                      src="@/assets/placeholder-logo.svg"
                      alt="placeholder logo"
                      class="broker-logo2"
                    /> -->
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    class="h-full w-auto text-light-gray"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <div
                  class="flex flex-grow flex-col justify-between gap-3 bg-alt-white p-4"
                >
                  <div>
                    <p class="text-base font-bold text-dark-gray">
                      {{ connection?.legal_name }}
                    </p>
                    <div class="pb-4">
                      <div
                        class="inline-block max-w-full overflow-hidden text-ellipsis"
                      >
                        <a
                          v-if="connection?.support_email"
                          :href="`mailto:${connection?.support_email}`"
                          class="group relative -left-2 flex transform items-center gap-1.5 rounded-full px-2 py-1 text-yellow-500 transition ease-in-out hover:translate-x-2 hover:bg-yellow-500 hover:bg-opacity-20"
                        >
                          <svg
                            class="h-3 w-3 flex-shrink-0 text-gray-over-dark group-hover:text-yellow-600"
                            viewBox="0 0 16 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            data-v-01ef35fe=""
                          >
                            <path
                              d="M2.66671 3.05762H13.3334C14.0667 3.05762 14.6667 3.65762 14.6667 4.39095V12.391C14.6667 13.1243 14.0667 13.7243 13.3334 13.7243H2.66671C1.93337 13.7243 1.33337 13.1243 1.33337 12.391V4.39095C1.33337 3.65762 1.93337 3.05762 2.66671 3.05762Z"
                              stroke="currentColor"
                              stroke-width="1.33333"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              data-v-01ef35fe=""
                            />
                            <path
                              d="M14.6667 4.39062L8.00004 9.05729L1.33337 4.39062"
                              stroke="currentColor"
                              stroke-width="1.33333"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              data-v-01ef35fe=""
                            />
                          </svg>
                          <span
                            class="text-ellipsis whitespace-nowrap text-xxs text-gray-over-dark group-hover:text-yellow-600"
                          >
                            {{ connection?.support_email }}
                          </span>
                        </a>
                      </div>
                    </div>
                  </div>
                  <button-main
                    variant="primary"
                    class="portal-btn group"
                    :disabled="connection?.sign_in_with_highway_link === null"
                    @click="
                      redirectToHighwayLink(
                        connection?.sign_in_with_highway_link
                      )
                    "
                  >
                    Connect
                    <svg
                      class="tester link-icon inline text-white"
                      width="25"
                      height="24"
                      viewBox="0 0 25 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.5 6H7.5C6.94772 6 6.5 6.44772 6.5 7V17C6.5 17.5523 6.94772 18 7.5 18H17.5C18.0523 18 18.5 17.5523 18.5 17V12"
                        stroke="currentColor"
                        stroke-width="2"
                      />
                      <path
                        d="M18.6748 5.78174L14.0262 10.4304M18.6748 5.78174L18.6748 9.43214M18.6748 5.78174L15.0244 5.78174"
                        stroke="currentColor"
                        stroke-width="2"
                      />
                    </svg>
                  </button-main>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  </banners>
</template>

<script type="text/javascript">
import moment from 'moment';
import ButtonMain from '@/components/Button.vue';
import Banners from '@/components/Banners.vue';
import CONFIG from '@/config';
import ConditionalWrapper from '../../components/ConditionalWrapper.vue';

export default {
  name: 'Carrier Brokers',
  components: {
    Banners,
    ButtonMain,
    ConditionalWrapper,
  },
  data() {
    return {
      preferredNameModalVisible: false,
      confirmCompanyEmailModalVisible: false,
      has_incomplete_eld: true,
    };
  },
  computed: {
    connections() {
      let conns = [...this.$store.state.brokers.connections];
      return conns.filter(
        (conn) => conn?.company_id !== 1 && conn?.status !== 'do_not_dispatch'
      );
    },
    loading() {
      return this.$store.state.loading.isLoading;
    },
    empty() {
      return this.$store.state.loading.isEmpty;
    },
    availableConnections() {
      const userConnections = this.connections;
      const allAvailableConnections =
        this.$store.state.brokers.availableConnections;

      const filteredConnections = allAvailableConnections?.filter(
        (connection) => {
          return !userConnections?.some(
            (userConnection) => userConnection?.company_id === connection?.id
          );
        }
      );

      return filteredConnections.length
        ? filteredConnections
        : allAvailableConnections;
    },
    currentUser() {
      return this.$store.state.users?.currentUser;
    },
    linkable_broker() {
      return this.$store.state.users.featureFlags?.onboarding_linkable_brokers
        ?.enabled;
    },
  },
  async created() {
    this.$store.commit('loading/loadingToggleOn', null, {
      root: true,
    });
    this.$store.commit('loading/emptyToggleOn', null, {
      root: true,
    });
    await this.$store.dispatch('brokers/getBrokers', {
      query: '/?q[s]=issuer_legal_name+asc',
    });
    await this.$store.dispatch('brokers/getAvailableConnections');
  },
  mounted() {
    // ask Amstrong about the IDV redirect
    if (this.$route?.query?.message) {
      this.$toast.open({
        message: this.$route.query?.message?.replaceAll('+', ' '),
        type: 'success',
        duration: 5000,
        dismissible: true,
        position: 'top-right',
      });

      this.$router.replace({
        query: {
          ...this.$route.query,
          message: undefined,
        },
      });
    }
    this.removeRedirectAfterIDV();
  },
  methods: {
    removeRedirectAfterIDV() {
      localStorage?.removeItem('redirectAfterIDV');
    },
    redirectToHighwayLink(link) {
      if (process.env.ENV_URL?.includes('localhost')) {
        link = link.replace('https://staging.highway.com', process.env.ENV_URL);
      }
      window.open(link, '_blank');
    },
    formatDate(date) {
      return moment(date).format('M/D/YY');
    },
    goToBrokerPortal(connection) {
      const { client_id, redirect_uri } = connection;
      let encodedURI = encodeURIComponent(redirect_uri);
      window.open(
        `${CONFIG.ENV_URL}${CONFIG.BASE_URL}oauth/authorizations?response_type=code&client_id=${client_id}&scope=onboarding&redirect_uri=${encodedURI}`,
        '_blank'
      );
    },
    isAssessmentPass(text) {
      return ['pass', 'partial_pass'].includes(text);
    },
  },
};
</script>

<style scoped>
dialog {
  border-radius: 10px;
}

.dialog-carrier-dashboard {
  margin: 5vh auto;
}

@media (min-width: 640px) {
  .dialog-carrier-dashboard {
    margin: 7.5vh auto;
  }
}

dialog::backdrop {
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(7px);
}

.header-text {
  font-size: 26px;
}
.subheader-text {
  font-size: 18px;
}

.notice-text {
  @apply text-center text-body-color;
  font-size: 13px;
}

.fail-status {
  padding: 4px 8px;
  background: #fff0e3;
  border-radius: 30px;
  font-size: 10px;
  /* width: 34px; */
  height: 24px;
  @apply text-orange-600;
}

.pass-status {
  padding: 4px 8px;
  background: #ddf4e3;
  border-radius: 30px;
  font-size: 10px;
  /* width: 40px; */
  height: 24px;
  @apply text-green-600;
}

.incomplete-status {
  padding: 4px 8px;
  background: #d6d7da;
  border-radius: 30px;
  font-size: 10px;
  /* width: 74px; */
  height: 24px;
}

::marker {
  font-size: 14px;
  margin-right: 20px;
  @apply text-body-color;
}

ul li {
  list-style-type: disc;
  list-style-position: inside;
  @apply mb-2 ml-6 text-1md;
  /* padding: 10px 0 10px 20px; */
  text-indent: -1em;
}

.link-icon {
  position: relative;
  bottom: 1px;
}

.notice-text {
  @apply text-center text-gray-over-dark;
  font-size: 16px;
}

/* .portal-btn[disabled] > .tester {
  @apply text-light-gray group-hover:text-light-gray;
} */

@keyframes wifiCircle {
  /* 0%, 6.5% {
    r: 0.25;
  }
  7.5%, 15% {
    r: 0.5;
  }
  16%, 23.5% {
    r: 0.75;
  }
  24.5% {
    r: 1.01;
  }
  100% {
    r: 1.01;
  } */
  0%,
  3.25% {
    r: 0.25;
  }
  3.75%,
  7.5% {
    r: 0.5;
  }
  8%,
  11.75% {
    r: 0.75;
  }
  12.25% {
    r: 1.01;
  }
  100% {
    r: 1.01;
  }
}

#clip0 circle {
  animation: wifiCircle 5s infinite ease-in-out;
}

@keyframes wifiSVG {
  0%,
  25%,
  62.5%,
  100% {
    opacity: 1;
  }
  43.75%,
  81.25% {
    opacity: 0.5;
  }
}

#eld-wifi {
  animation: wifiSVG 5s infinite ease-in-out;
}

.hide-small {
  display: none;
}
@media (min-width: 360px) {
  .hide-small {
    display: initial;
  }
}
</style>
